import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X } from 'react-feather';
import { useAuth } from '../../context/AuthContext';
import { useSearch } from '../../context/SearchContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { BREAKPOINTS } from '../../constants/breakpoints';
import { NotificationBell } from '../notifications/NotificationBell';

const DashboardHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const { searchQuery, setSearchQuery, clearSearch } = useSearch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.tablet}px)`);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Only show search for contractors
  const isContractor = user && 'type' in user && (user as any).type === 'contractor';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header style={{
      padding: isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)',
      backgroundColor: 'var(--color-bg-primary)',
      borderBottom: '1px solid var(--color-border-default)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--space-3)',
        width: '100%'
      }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexShrink: 0 }}>
          <img
            src="/logo192.png"
            alt="FXTR Logo"
            style={{
              height: '40px',
              width: '40px',
              borderRadius: 'var(--radius-md)'
            }}
          />
          {!isMobile && (
            <span style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)'
            }}>
              FXTR Connect
            </span>
          )}
        </Link>

        {/* Search bar - contractors only */}
        {isContractor && !isMobile && (
          <div style={{
            flex: 1,
            maxWidth: '400px',
            margin: '0 var(--space-4)'
          }}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Search
                size={18}
                style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  color: 'var(--color-text-muted)',
                  pointerEvents: 'none'
                }}
              />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  paddingLeft: '40px',
                  paddingRight: searchQuery ? '36px' : '12px',
                  border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--input-text)',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'var(--transition-fast)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--input-focus-border)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--input-border)';
                }}
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  style={{
                    position: 'absolute',
                    right: 'var(--space-2)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 'var(--space-1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-muted)'
                  }}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Mobile search - icon that opens full-width input */}
        {isContractor && isMobile && mobileSearchOpen && (
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            backgroundColor: 'var(--color-bg-primary)',
            display: 'flex',
            alignItems: 'center',
            padding: 'var(--space-3) var(--space-4)',
            gap: 'var(--space-2)',
            zIndex: 10
          }}>
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              style={{
                flex: 1,
                padding: '10px 12px',
                border: '1px solid var(--input-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)',
                color: 'var(--input-text)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <button
              onClick={() => {
                setMobileSearchOpen(false);
                if (!searchQuery) clearSearch();
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 'var(--space-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-secondary)'
              }}
              aria-label="Close search"
            >
              <X size={20} />
            </button>
          </div>
        )}

        <nav>
          <ul style={{
            display: 'flex',
            gap: isMobile ? 'var(--space-2)' : 'var(--space-5)',
            listStyle: 'none',
            alignItems: 'center',
            margin: 0,
            padding: 0
          }}>
            {/* Mobile search icon for contractors */}
            {isContractor && isMobile && !mobileSearchOpen && (
              <li>
                <button
                  onClick={() => setMobileSearchOpen(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 'var(--space-2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-secondary)',
                    minHeight: '44px',
                    minWidth: '44px'
                  }}
                  aria-label="Open search"
                >
                  <Search size={20} />
                </button>
              </li>
            )}
            {!isMobile && (
              <li>
                <Link to="/" style={{
                  textDecoration: 'none',
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)'
                }}>
                  Home
                </Link>
              </li>
            )}
            <li style={{
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-text-primary)',
              fontSize: isMobile ? 'var(--text-xs)' : 'var(--text-sm)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: isMobile ? '120px' : 'none'
            }}>
              <Link
                to={isContractor ? `/contractor/${(user as any).id}` : '#'}
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                {(() => {
                  if (!user) return 'Dashboard';
                  if ('name' in user && user.name) {
                    return `Hello, ${user.name.split(' ')[0]}`;
                  }
                  if ((user as any).firstName) {
                    return `Hello, ${(user as any).firstName}`;
                  }
                  return 'Dashboard';
                })()}
              </Link>
            </li>
            <li>
              <NotificationBell />
            </li>
            <li>
              <button
                onClick={handleLogout}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                  padding: 'var(--space-2)',
                  font: 'inherit',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  minHeight: '44px'
                }}
              >
                Logout
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default DashboardHeader;
